import React, { useState } from "react";
import {
  CardElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { processPayment } from "../services/PaymentService";

// Load Stripe with publishable key
const stripePromise = loadStripe(
  "pk_live_51RbQ3qRs1rLzJ1eXpJGJQtlieMsuW7DC1nNAYeVdImmZUuJo7IydNKdhNzzlyELCOEe44RIAfj31aJNIlYAQ6lkY00pEJPP0Ul"
);

interface StripePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  billId: number;
  amount: number;
  userName: string;
  onPaymentSuccess: () => void;
}

const CheckoutForm: React.FC<{
  billId: number;
  amount: number;
  userName: string;
  onClose: () => void;
  onPaymentSuccess: () => void;
}> = ({ billId, amount, userName, onClose, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [status, setStatus] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus("");

    if (!stripe || !elements) {
      setStatus("❌ Stripe not loaded");
      setIsLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      // Step 1: Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement!,
      });

      if (error) {
        setStatus(`❌ Card Error: ${error.message}`);
        setIsLoading(false);
        return;
      }

      // Step 2: Send paymentMethod.id to backend
      const paymentData = {
        paymentMethodId: paymentMethod!.id,
        billId: billId,
      };

      const response = await processPayment(paymentData);
      console.log("Payment response:", response);

      if (response?.data?.result === "success") {
        setStatus("✅ Payment successful!");
        setTimeout(() => {
          onPaymentSuccess();
          onClose();
        }, 2000);
      } else if (
        response?.data?.message?.startsWith("Additional action required")
      ) {
        // Step 3: Handle 3D Secure
        const clientSecret = response.data.message.split(": ")[1];
        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(clientSecret);

        if (confirmError) {
          setStatus(`❌ 3D Secure Error: ${confirmError.message}`);
        } else if (paymentIntent?.status === "succeeded") {
          setStatus("✅ Payment successful!");
          setTimeout(() => {
            onPaymentSuccess();
            onClose();
          }, 2000);
        } else {
          setStatus(`❌ Payment failed: ${paymentIntent?.status || "unknown"}`);
        }
      } else {
        setStatus(
          `❌ ${
            response?.data?.message ||
            (response?.data?.result && response?.data?.result !== "success"
              ? response?.data?.result
              : "Payment failed")
          }`
        );
      }
    } catch (error) {
      // Handle different error types
      let errorMessage = "Payment failed";
      console.log("error", error);
      if (typeof error === "string") {
        // Case-insensitive error matching
        const err = error.toLowerCase();
        if (err.includes("your card has insufficient funds")) {
          errorMessage =
            "Your card has insufficient funds. Please try a different card or contact your bank.";
        } else if (err.includes("your card was declined")) {
          errorMessage = "Your card was declined. Please try a different card.";
        } else if (err.includes("insufficient_funds")) {
          errorMessage =
            "Insufficient funds. Please try a different card or contact your bank.";
        } else if (err.includes("expired_card")) {
          errorMessage = "Card has expired. Please use a different card.";
        } else if (err.includes("incorrect_number")) {
          errorMessage = "Incorrect card number. Please check and try again.";
        } else if (err.includes("invalid_cvc")) {
          errorMessage = "Invalid CVC. Please check and try again.";
        } else if (err.includes("card_declined")) {
          errorMessage =
            "Card declined. Please check your card details or try a different card.";
        } else {
          // Remove technical details and show user-friendly message
          errorMessage = error.split(";")[0].trim();
        }
      } else if (error instanceof Error) {
        // Standard Error object
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null) {
        // Error object with custom structure
        errorMessage =
          (error as any).message || (error as any).result || "Payment failed";
      }

      setStatus(`❌ ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Pay Bill - {userName}</h3>
          <button onClick={onClose} className="close-btn">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="payment-form">
          <div className="amount-display">
            <h4>Amount to Pay: €{amount.toFixed(2)}</h4>
          </div>

          <div className="card-element-container">
            <label>Card Details</label>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": { color: "#aab7c4" },
                  },
                  invalid: { color: "#9e2146" },
                },
              }}
            />
          </div>

          <button
            type="submit"
            disabled={!stripe || isLoading}
            className={`pay-button ${isLoading ? "loading" : ""}`}
          >
            {isLoading ? "Processing..." : `Pay €${amount.toFixed(2)}`}
          </button>

          {status && (
            <div
              className={`status-message ${
                status.startsWith("✅") ? "success" : "error"
              }`}
            >
              {status}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const StripePaymentModal: React.FC<StripePaymentModalProps> = ({
  isOpen,
  onClose,
  billId,
  amount,
  userName,
  onPaymentSuccess,
}) => {
  if (!isOpen) return null;

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        billId={billId}
        amount={amount}
        userName={userName}
        onClose={onClose}
        onPaymentSuccess={onPaymentSuccess}
      />
    </Elements>
  );
};

export default StripePaymentModal;
